// src/components/mitchRoom/revenueAnalytics/OrdersTable.js
import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Copy, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { formatCurrency } from 'utils/format';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Payment source brand colours (tinted bg + solid text)
const SOURCE_BADGE_STYLES = {
  stripe: { backgroundColor: '#6772e520', color: '#6772e5' },
  woocommerce: { backgroundColor: '#96588a20', color: '#96588a' },
  default: { backgroundColor: '#9e9e9e20', color: '#616161' },
};

const getStatusVariant = (status) => {
  switch (status?.toLowerCase()) {
    case 'paid':
    case 'completed':
      return 'success';
    case 'pending':
    case 'processing':
      return 'warning';
    case 'failed':
    case 'cancelled':
      return 'destructive';
    case 'refunded':
      return 'info';
    default:
      return 'outline';
  }
};

const getTypeLabel = (type) => {
  if (!type) return '-';
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Who the buyer was at purchase time. Same colours as the charts.
// 'expansion' is the case the old order_type could not express: an existing
// active subscriber buying an add-on (e.g. the Pre-Season Pack).
const BUYER_CLASS_STYLES = {
  new: { label: 'New', color: '#1baf7a' },
  winback: { label: 'Win-back', color: '#2a78d6' },
  expansion: { label: 'Expansion', color: '#eb6834' },
  upgrade: { label: 'Upgrade', color: '#eb6834' },
  renewal: { label: 'Recurring', color: '#eda100' },
  unattributed: { label: 'Unattributed', color: '#9e9e9e' },
};

const HEADER_TH =
  'whitespace-nowrap border-b border-borderGrey bg-white p-2 text-left text-token-md font-semibold';

export default function OrdersTable({
  orders,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  loading,
}) {
  // Client-side quick filter over the currently loaded page only — no refetch.
  const [filter, setFilter] = useState('');

  const displayedOrders = useMemo(() => {
    const rows = orders?.orders || [];
    if (!filter) return rows;
    const f = filter.toLowerCase();
    return rows.filter(
      (o) =>
        o.customer_name?.toLowerCase().includes(f) ||
        o.customer_email?.toLowerCase().includes(f) ||
        o.order_id?.toLowerCase().includes(f)
    );
  }, [orders, filter]);

  const handleCopyOrderId = async (orderId) => {
    try {
      await navigator.clipboard.writeText(orderId);
      toast.success('Order ID copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy order ID');
    }
  };

  if (loading && !orders) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="mb-3 text-token-xl font-semibold">Recent Orders</h3>
        <div className="flex flex-col gap-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!orders || !orders.orders || orders.orders.length === 0) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="mb-3 text-token-xl font-semibold">Recent Orders</h3>
        <p className="py-6 text-center text-token-md text-muted-foreground">No orders found</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-token-large border border-borderGrey bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 border-b border-borderGrey px-4 py-3">
        <h3 className="text-token-xl font-semibold">Recent Orders</h3>
        <span className="text-token-sm text-muted-foreground">
          {orders.total_count.toLocaleString()} total orders
        </span>
        <div className="relative ml-auto">
          <Search
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            placeholder="Filter this page..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-[220px] pl-8 text-sm"
          />
        </div>
        {onPageSizeChange && (
          <Select
            value={String(pageSize || 25)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger className="h-9 w-[110px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} / page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={HEADER_TH}>Order ID</th>
              <th className={HEADER_TH}>Date</th>
              <th className={HEADER_TH}>Customer</th>
              <th className={HEADER_TH}>Buyer</th>
              <th className={HEADER_TH}>Product</th>
              <th className={HEADER_TH}>Type</th>
              <th className={`${HEADER_TH} text-right`}>Amount</th>
              <th className={HEADER_TH}>Status</th>
              <th className={HEADER_TH}>Source</th>
            </tr>
          </thead>
          <tbody>
            {displayedOrders.length === 0 ? (
              <tr>
                <td colSpan={9} className="p-6 text-center text-token-md text-muted-foreground">
                  No orders on this page match "{filter}"
                </td>
              </tr>
            ) : (
              displayedOrders.map((order) => (
                <tr key={order.order_id} className="group hover:bg-neutral-50">
                  <td className="border-b border-borderGrey p-2">
                    <button
                      type="button"
                      onClick={() => handleCopyOrderId(order.order_id)}
                      title={`${order.order_id}\nClick to copy`}
                      className="inline-flex items-center gap-1 font-mono text-token-sm hover:text-brand-purple1"
                    >
                      {order.order_id.slice(0, 8)}...
                      <Copy
                        size={11}
                        className="opacity-0 transition-opacity group-hover:opacity-60"
                        aria-hidden="true"
                      />
                    </button>
                  </td>
                  <td className="whitespace-nowrap border-b border-borderGrey p-2">
                    {order.order_date
                      ? new Date(order.order_date).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: '2-digit',
                        })
                      : '-'}
                  </td>
                  <td className="max-w-[220px] truncate border-b border-borderGrey p-2">
                    {order.customer_name || order.customer_email || '-'}
                  </td>
                  <td className="border-b border-borderGrey p-2">
                    {order.buyer_class ? (
                      <Badge
                        variant="outline"
                        className="border-transparent text-white"
                        style={{
                          backgroundColor:
                            BUYER_CLASS_STYLES[order.buyer_class]?.color || '#9e9e9e',
                        }}
                      >
                        {BUYER_CLASS_STYLES[order.buyer_class]?.label || order.buyer_class}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="max-w-[180px] truncate border-b border-borderGrey p-2 text-muted-foreground">
                    {order.product_name || '-'}
                  </td>
                  <td className="border-b border-borderGrey p-2 text-muted-foreground">
                    {getTypeLabel(order.order_type)}
                  </td>
                  <td className="border-b border-borderGrey p-2 text-right font-medium tabular-nums">
                    {order.amount_gbp === null || order.amount_gbp === undefined
                      ? '-'
                      : formatCurrency(order.amount_gbp)}
                  </td>
                  <td className="border-b border-borderGrey p-2">
                    <Badge variant={getStatusVariant(order.status)}>{order.status}</Badge>
                  </td>
                  <td className="border-b border-borderGrey p-2">
                    <Badge
                      variant="outline"
                      className="border-transparent"
                      style={SOURCE_BADGE_STYLES[order.source] || SOURCE_BADGE_STYLES.default}
                    >
                      {order.source}
                    </Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {orders.total_pages > 1 && (
        <div className="flex items-center justify-between border-t border-borderGrey px-4 py-3">
          <span className="text-token-sm text-muted-foreground">
            Page {page} of {orders.total_pages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft />
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= orders.total_pages}
            >
              Next
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
